import { useRef, useEffect, useState } from 'react';
import Artist from './artist';
import ArtistWebgl, { Sampler, StateType } from './artistWebgl';
import Animator from './animator';
import { pixelsWithNoise, particleWithAbberationFs, alphaChannelFs } from './shaders';
import Layout from './layout';
import Greeting from './greeting';
import defineViewMode from './viewMode';
import { PointVector2D } from './geometry';
import { Page } from './App';
import { Link, Route, Routes } from 'react-router-dom';
import About from './about';
import { ChildComponentRef } from './childComponent';
import Contacts from './contacts';
import CV from './cv';
import { ParameterBox, scenes } from './scenes';

interface LandingProps {
    setPage: (page: Page) => void;
}

const Landing = ({setPage}: LandingProps) => {
    const [bg, setBg] = useState<Artist | null>(null);
    const [cursor, setCursor] = useState<Artist | null>(null);
    
    const bgCanvasRef = useRef<HTMLCanvasElement | null>(null);
    const cursorCanvasRef = useRef<HTMLCanvasElement | null>(null);
    const childComponentRef = useRef<ChildComponentRef>(null);
    
    let setChildComponentBrightness = (cb: number) => childComponentRef.current!.updateChildComponentBrightness(cb);
    let setChildComponentFontSize = (cfs: number) => childComponentRef.current!.updateChildComponentFontSize(cfs);
    let setChildComponentLocation = (cl: {x:number, y:number}) => childComponentRef.current!.updateChildComponentLocation(cl);
    let setChildComponentSize = (cs: {x:number, y:number}) => childComponentRef.current!.updateChildComponentSize(cs);
    
    let viewMode = defineViewMode();

    function openFullscreen() {
        const elem = document.documentElement as any;
        if (elem.requestFullscreen) {
            elem.requestFullscreen();
        } else if (elem.webkitRequestFullscreen) { /* Safari */
        elem.webkitRequestFullscreen();
        } else if (elem.msRequestFullscreen) { /* IE11 */
        elem.msRequestFullscreen();
        }
    }

    function closeFullscreen() {
        const doc = document as any;
        if (doc.exitFullscreen) {
            doc.exitFullscreen();
        } else if (doc.webkitExitFullscreen) { /* Safari */
            doc.webkitExitFullscreen();
        } else if (doc.msExitFullscreen) { /* IE11 */
            doc.msExitFullscreen();
        }
    }

    useEffect(() => {
        if (bgCanvasRef.current && cursorCanvasRef.current) {
            setBg(new Artist(bgCanvasRef.current));
            setCursor(new Artist(cursorCanvasRef.current));
        }
    }, []);
    
    useEffect(() => {
        const animator = new Animator(false);
        const layoutCanvas = document.createElement("canvas");
        const layoutArtist = new Artist(layoutCanvas);
        const layout = new Layout(layoutArtist, animator.elapsed, 'greetingDesktop', {setChildComponentBrightness, setChildComponentFontSize, setChildComponentLocation, setChildComponentSize});
    
        const webglCanvasBg = document.createElement("canvas");
        const webglBgSamplers: Array<Sampler> = [
            {
                states: [StateType.FadeOut],
                sampleType: 'Self',
                shapeSampler: false,
            }
        ]

        const parameters = [{
            name: 'Pixels',
            type: 'horizontalSlider',
            category: 'global',
            parameters: [
                {
                    name: 'Size',
                    value: 0.3,
                },
            ]
        } as ParameterBox ]

        const artistWebglBg = new ArtistWebgl(webglCanvasBg, undefined, pixelsWithNoise, parameters, layoutCanvas, webglBgSamplers);
    
        const webglCanvasParticle = document.createElement("canvas");
        const webglParticleSamplers: Array<Sampler> = [
            {
                states: [StateType.FadeIn, StateType.Normal, StateType.FadeOut],
                sampleType: 'Self',
                shapeSampler: false,
            }
        ]
        const artistWebglParticle = new ArtistWebgl(webglCanvasParticle, undefined, particleWithAbberationFs, parameters, undefined, webglParticleSamplers);
    
        const webglCanvasParticleAlpha = document.createElement("canvas");
        const webglParticleAlphaSamplers: Array<Sampler> = [
            {
                states: [StateType.FadeIn, StateType.Normal, StateType.FadeOut],
                sampleType: 'Canvas',
                shapeSampler: false,
                sampleSource: artistWebglParticle.canvas,
            }
        ]
        const artistWebglParticleAlpha = new ArtistWebgl(webglCanvasParticleAlpha, undefined, alphaChannelFs, undefined, undefined, webglParticleAlphaSamplers);
    
        let cursorStillTime = 0;
        let touchStartY = 0;
        let touchCurrentY = 0;
        let pointedElement: HTMLElement;
    
        window.onresize = function() { 
            if (bg && cursor) {
                viewMode = defineViewMode();
                childComponentRef.current!.updateViewMode(viewMode, window.innerWidth, window.innerHeight);
                bg.resizeImageToCanvasSize();
                cursor.resizeImageToCanvasSize();
                layoutArtist.updateCanvasSize(bg.canvasWidth, bg.canvasHeight);
                artistWebglBg.updateCanvasSize(bg.canvasWidth, bg.canvasHeight);
                artistWebglParticle.updateCanvasSize(bg.canvasWidth, bg.canvasHeight);
                artistWebglParticleAlpha.updateCanvasSize(bg.canvasWidth, bg.canvasHeight);
            }
        }

        if (bg && cursor) {
            bg.resizeImageToCanvasSize();
            cursor.resizeImageToCanvasSize();
            //layout.childDiv = childComponentRef.current!.divRef.current;
            layout.cursor = cursor?.canvas;
            layoutArtist.updateCanvasSize(bg.canvasWidth, bg.canvasHeight);
            artistWebglBg.updateCanvasSize(bg.canvasWidth, bg.canvasHeight);
            artistWebglParticle.updateCanvasSize(bg.canvasWidth, bg.canvasHeight);
            artistWebglParticleAlpha.updateCanvasSize(bg.canvasWidth, bg.canvasHeight);
            bg.createFlowfield();
            bg.flowfield!.setEdgesToCenter();
            bg.flowfield!.createParticles(1, 2, false);

            document.onpointerdown = (event) => {
                const cursorLocation = (viewMode.size == 'desktop' && viewMode.graphics == 'high') ? 
                    {x: bg.flowfield!.particles[0].location.x, y: bg.flowfield!.particles[0].location.y} :
                    {x: event.clientX, y: event.clientY}
                cursor.canvas.style.pointerEvents = 'none';
                const element = cursorLocation.x && cursorLocation.y? document.elementFromPoint(cursorLocation.x, cursorLocation.y) as HTMLElement: undefined;
                cursor.canvas.style.pointerEvents = 'auto';
                if (element && element.tagName === 'A' && layout.state != 'Shift' && !element.className.includes('social')) {
                    const originalHandler = element.onclick;
                    element.onclick = (e) => e.preventDefault();
                    layout.changeState('Shift', animator.elapsed)
                    setTimeout(() => {
                        element.onclick = originalHandler;
                        element.click();
                        layout.setScrollValue(0);
                    }, 500)
                } else if (element && element.tagName === 'BUTTON') {
                    event.preventDefault();
                    setTimeout(() => element.click(), 0)
                    if (element.className.includes('CV')) layout.scrollValue = 0;
                } else if (element && element.className.includes('social')) {
                    element.click()
                }
                layout.getProps(animator.elapsed, event.clientX, event.clientY)
                if (layout.scrollPosition.distanceTo(new PointVector2D(event.clientX, event.clientY)) < 20) {
                    layout.scrollDragging = true;
                }
                if (layout.fullScreenButtonOver) {
                    if(!document.fullscreenElement){ 
                        openFullscreen();
                    } else { 
                        closeFullscreen();
                    }
                }
                if (layout.exploreButtonOver) {
                    layout.changeState('FadeOut', animator.elapsed);
                    setTimeout(() => {
                        artistWebglBg.changeState(StateType.FadeOut, animator.elapsed);
                    }, 500)
                    setTimeout(() => {
                        animator.animationPause();
                        setPage('Main');
                    }, 5000)
                }
            }
            document.onpointerup = (event) => {
                layout.scrollDragging = false;
            }
            document.onpointermove = (event) => {
                const cursorLocation = (viewMode.size == 'desktop' && viewMode.graphics == 'high') ? 
                    {x: bg.flowfield!.particles[0].location.x, y: bg.flowfield!.particles[0].location.y} :
                    {x: event.clientX, y: event.clientY}
                cursor.canvas.style.pointerEvents = 'none';
                const element = cursorLocation.x && cursorLocation.y? document.elementFromPoint(cursorLocation.x, cursorLocation.y) as HTMLElement: undefined;
                cursor.canvas.style.pointerEvents = 'auto';
                if (element && (element.tagName === 'A' || element.tagName === 'BUTTON')) {
                    if (element !== pointedElement) {
                        pointedElement?.classList.remove('hover');
                        pointedElement = element;
                    }
                    element.classList.add('hover');
                } else if (element && (element.tagName === 'INPUT' || element.tagName === 'TEXTAREA')) {
                    cursor.canvas.style.pointerEvents = 'none';
                    layout.setCursorOverride('text');
                } else if(element && (element.tagName === 'LI')) {
                    if (element !== pointedElement) {
                        pointedElement?.classList.remove('over');
                        pointedElement = element;
                    }
                    element.classList.add('over');
                } else if(element && (element.tagName === 'SMALL' || element.tagName === 'SPAN')) {
                    if (element.parentElement !== pointedElement) {
                        pointedElement?.classList.remove('over');
                        pointedElement = element.parentElement!;
                    }
                    element.parentElement!.classList.add('over');
                } else {
                    cursor.canvas.style.pointerEvents = 'auto';
                    layout.setCursorOverride(null);
                    pointedElement?.classList.remove('hover');
                    pointedElement?.classList.remove('over');
                }
                cursorStillTime = 0;
                bg.flowfield?.setParticlesFollowCursor(true);
                artistWebglBg.updateCursorPosition(event.clientX, event.clientY);
                artistWebglParticle.updateCursorPosition(event.clientX, event.clientY);
                bg.updateCursorPosition(event.clientX, event.clientY);
                if (layout.scrollDragging == true) {
                    layout.setScrollValueByPointerLocation(event.clientY);
                }
            }
            document.onpointerleave = (event) => {
                bg.flowfield?.setParticlesFollowCursor(false);
            }
            document.onwheel = (event) => {
                layout.addToScrollValue(event.deltaY);
            }
            document.ontouchstart = (event) => {
                event.preventDefault();
                touchStartY = event.touches[0].clientY;
                layout.getProps(animator.elapsed, event.touches[0].clientX, event.touches[0].clientY)
                
            }
            document.ontouchmove = (event) => {
                event.preventDefault();
                touchCurrentY = event.touches[0].clientY;
                let touchScrollAmount = touchStartY - touchCurrentY;
                layout.addToScrollValue(touchScrollAmount);
                touchStartY = touchCurrentY;
                if (layout.scrollDragging == true) {
                    layout.setScrollValueByPointerLocation(event.touches[0].clientY);
                }
                
            }
            document.ontouchend = (event) => {
                if (layout.state == 'Shift') {
                    event.preventDefault();
                }
                layout.scrollDragging = false;
            }
            animator.animationSequence = () => {
                layout.childDiv = childComponentRef.current!.divRef.current;
                cursorStillTime += 1;
                if (cursorStillTime > animator.fps * 10) bg.flowfield?.setParticlesFollowCursor(false);


                if (animator.fps) {
                    viewMode = defineViewMode(animator.fps);
                }
                const cursorAnimation = viewMode.size == 'desktop' && viewMode.graphics == 'high';
                if (!cursorAnimation) {
                    cursor.clear();
                    //cursor.canvas.style.pointerEvents = 'none';
                    
                    layout.setCursorOverride('auto');
                    //bg.canvas.style.cursor = "auto";
                    //cursor.canvas.style.cursor = "auto";
                } else {
                    //cursor.canvas.style.pointerEvents = 'auto';
                    layout.setCursorOverride(null);
                    //bg.canvas.style.cursor = "none";
                    //cursor.canvas.style.cursor = "none";
                }


                /* if (animator.fps) {
                    const newViewMode = defineViewMode(animator.fps);
                    if (newViewMode.graphics != viewMode.graphics) {
                        if (newViewMode.graphics != 'high') {
                            cursor.canvas.style.pointerEvents = 'none';
                            bg.canvas.style.cursor = "auto";
                            cursor.canvas.style.cursor = "auto";
                        }
                        else {
                            cursor.canvas.style.pointerEvents = 'auto';
                            bg.canvas.style.cursor = "none";
                            cursor.canvas.style.cursor = "none";
                        }
                    }
                    viewMode = newViewMode;
                } */

                //if (viewMode.graphics == 'low') cursor.canvas.style.pointerEvents = 'none';
                childComponentRef.current!.updateViewMode(viewMode, document.documentElement.clientWidth, document.documentElement.clientHeight);
                artistWebglBg.drawScene(animator.elapsed, bg.flowfield?.particles[0].location.x, bg.flowfield?.particles[0].location.y);
                bg.drawImage(artistWebglBg.canvas);
                //bg.drawText(animator.fps.toString());
                //bg.drawText(document.documentElement.clientWidth.toString());
                if (viewMode.size == 'desktop' && (viewMode.graphics == 'high' || viewMode.graphics == 'medium')) {
                    switch (childComponentRef.current!.name) {
                        case 'Greeting':
                            layout.setLayoutType('greetingDesktop');
                            break;
                        case 'About':
                            layout.setLayoutType('aboutDesktop');
                            break;
                        case 'CV':
                            layout.setLayoutType('cvDesktop');
                            break;
                        case 'Contacts':
                            layout.setLayoutType('contactsDesktop');
                            break;
                    }
                    if (viewMode.graphics == 'medium') {
                        cursor.clear();
                        layout.draw(animator.elapsed, bg.cursorX, bg.cursorY);
                    }
                    else {
                        layout.draw(animator.elapsed, bg.flowfield!.particles[0].location.x, bg.flowfield!.particles[0].location.y);
                        artistWebglParticle.drawScene(animator.elapsed, bg.flowfield?.particles[0].location.x, bg.flowfield?.particles[0].location.y);
                        artistWebglParticleAlpha.drawScene(animator.elapsed);
                        cursor.clear();
                        cursor.drawImage(artistWebglParticleAlpha.canvas);
                        bg.flowfield?.setTime(animator.elapsed / 500);
                        bg.flowfield?.moveParticles(animator.fps, bg.cursorX, bg.cursorY);
                    }
                }
                else {
                    switch (childComponentRef.current!.name) {
                        case 'Greeting':
                            layout.setLayoutType('greetingMobile');
                            break;
                        case 'About':
                            layout.setLayoutType('aboutMobile');
                            break;
                        case 'CV':
                            layout.setLayoutType('cvMobile');
                            break;
                        case 'Contacts':
                            layout.setLayoutType('contactsMobile');
                            break;
                    }
                    layout.draw(animator.elapsed, bg.cursorX, bg.cursorY);
                }
            }
            animator.animationStart();
        }
        return () => {
            document.onpointerdown = null;
            document.onpointerup = null;
            document.onpointermove = null;
            document.onpointerleave = null;
            document.onwheel = null;
            document.ontouchstart = null;
            document.ontouchmove = null;
            document.ontouchend = null;
        };
    }, [bg]);
    return (
        <>
            <canvas ref={bgCanvasRef} style={{width: "100vw", height: "100dvh", position: 'absolute', zIndex: 0}}/>
            <Routes>
                <Route path='*' element={<Greeting ref={childComponentRef}/>}/>
                <Route path='/about' element={<About ref={childComponentRef}/>}/>
                <Route path='/cv' element={<CV ref={childComponentRef}/>}/>
                <Route path='/contacts' element={<Contacts ref={childComponentRef}/>}/>
            </Routes>
            <canvas ref={cursorCanvasRef} style={{width: "100vw", height: "100dvh", position: 'absolute', zIndex: 2, opacity: 0}}/>
        </>
    )
}

export default Landing